<template>
  <div class="heatmap-panel" v-if="map">
    <div class="heatmap-panel-title">
      <i class="el-icon-menu"></i>
      <span>功能 - 船舶轨迹线</span>
      <i class="el-icon-close"
         style="position: absolute; right: 15px; cursor: pointer;"
         @click="$emit('close')"></i>
    </div>
    <div class="heatmap-panel-content">
      <!-- 时间范围 -->
      <div class="form-row">
        <label class="form-label">时间范围（最长30天）</label>
        <el-date-picker
          v-model="timeRange"
          type="datetimerange"
          value-format="yyyy-MM-dd HH:mm:ss"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          :picker-options="pickerOptions"
          size="small"
          style="width: 100%;">
        </el-date-picker>
      </div>

      <!-- 参数设置 -->
      <div class="form-row-inline">
        <div class="form-row-item">
          <label class="form-label">轨迹抽稀(°)</label>
          <el-input-number
            v-model="trackTolerance"
            :step="0.00005"
            :min="0.00005"
            :max="0.002"
            :precision="5"
            size="small"
            style="width: 100%;">
          </el-input-number>
        </div>
      </div>

      <!-- 区域范围 -->
      <div class="form-row">
        <div class="form-label-row">
          <label class="form-label">区域范围</label>
          <el-checkbox v-model="useMapBounds" size="small">使用当前地图视野</el-checkbox>
        </div>
        <div class="bounds-info" v-if="useMapBounds && currentBounds">
          经度: {{ currentBounds.minLon.toFixed(4) }} ~ {{ currentBounds.maxLon.toFixed(4) }}
          <br>
          纬度: {{ currentBounds.minLat.toFixed(4) }} ~ {{ currentBounds.maxLat.toFixed(4) }}
        </div>
        <div v-else class="bounds-manual">
          <div class="bounds-input-row">
            <el-input v-model="manualBounds.minLon" placeholder="最小经度" size="small"></el-input>
            <el-input v-model="manualBounds.maxLon" placeholder="最大经度" size="small"></el-input>
          </div>
          <div class="bounds-input-row">
            <el-input v-model="manualBounds.minLat" placeholder="最小纬度" size="small"></el-input>
            <el-input v-model="manualBounds.maxLat" placeholder="最大纬度" size="small"></el-input>
          </div>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div class="button-row">
        <el-button
          type="primary"
          size="small"
          :loading="loading"
          :disabled="!timeRange || timeRange.length !== 2"
          @click="generateHeatmap">
          <i class="el-icon-magic-stick"></i> 生成轨迹线
        </el-button>
        <el-button
          size="small"
          :disabled="!trackLayer"
          @click="clearHeatmap">
          清除
        </el-button>
        <el-button
          type="success"
          size="small"
          :disabled="!trackLayer"
          :loading="exporting"
          @click="exportImage">
          <i class="el-icon-download"></i> 导出图片
        </el-button>
      </div>

      <!-- 状态信息 -->
      <div class="status-area" v-if="statusText">
        <i class="el-icon-info"></i>
        <span>{{ statusText }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import L from 'leaflet';
import html2canvas from 'html2canvas';
import { getVesselTracks } from '@/api/heatmap';

// 最大查询时间范围（天），与后端 HeatmapController 的 MAX_DATE_RANGE_DAYS 保持一致
const MAX_RANGE_DAYS = 30;
// 轨迹线查询最大区域面积（平方度），与后端 HeatmapController 的 MAX_TRACK_AREA_DEG2 保持一致
const MAX_TRACK_AREA = 0.1;
// 轨迹线按"共轨迹"密度着色：稀疏(黄) → 多船重复经过的主干航道(红)
const TRACK_DENSITY_COLORS = ['#ffd400', '#ffc400', '#ffab00', '#ff8f00', '#ff6f00', '#f6400d', '#d81e1e'];
// 各密度等级的线条透明度：主干核心更醒目，外围略淡但仍清晰可见
const TRACK_DENSITY_OPACITY = [0.5, 0.53, 0.57, 0.62, 0.68, 0.76, 0.85];
// 统计航迹密度的网格大小（度），约130米
const TRACK_DENSITY_CELL_DEG = 0.0012;
// 单条线段在屏幕上的最大长度（像素）：超过则不绘制该线段，避免出现明显的长直线
const TRACK_MAX_SEGMENT_PX = 200;

export default {
  name: 'HeatmapPanel',
  props: {
    map: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      timeRange: null,
      trackTolerance: 0.0002,
      useMapBounds: true,
      manualBounds: {
        minLon: '',
        maxLon: '',
        minLat: '',
        maxLat: ''
      },
      loading: false,
      exporting: false,
      statusText: '',
      trackLayer: null,
      trackRenderer: null,
      trackCount: 0,
      pickerOptions: {
        shortcuts: [
          {
            text: '最近1天',
            onClick(picker) {
              const end = new Date();
              const start = new Date(end.getTime() - 1 * 24 * 60 * 60 * 1000);
              picker.$emit('pick', [start, end]);
            }
          },
          {
            text: '最近7天',
            onClick(picker) {
              const end = new Date();
              const start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
              picker.$emit('pick', [start, end]);
            }
          },
          {
            text: '最近30天',
            onClick(picker) {
              const end = new Date();
              const start = new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000);
              picker.$emit('pick', [start, end]);
            }
          }
        ],
        // 限制可选日期：禁用30天之前的日期与未来日期，保证时间跨度不超过30天
        disabledDate(time) {
          const maxRangeMs = MAX_RANGE_DAYS * 24 * 60 * 60 * 1000;
          const now = Date.now();
          return time.getTime() < now - maxRangeMs || time.getTime() > now;
        }
      }
    };
  },
  computed: {
    currentBounds() {
      if (!this.map) return null;
      try {
        const bounds = this.map.getBounds();
        return {
          minLon: bounds.getWest(),
          maxLon: bounds.getEast(),
          minLat: bounds.getSouth(),
          maxLat: bounds.getNorth()
        };
      } catch (e) {
        return null;
      }
    }
  },
  beforeDestroy() {
    this.clearHeatmap();
  },
  methods: {
    /**
     * 获取查询区域范围
     */
    getQueryBounds() {
      if (this.useMapBounds && this.currentBounds) {
        return this.currentBounds;
      }
      return {
        minLon: parseFloat(this.manualBounds.minLon),
        maxLon: parseFloat(this.manualBounds.maxLon),
        minLat: parseFloat(this.manualBounds.minLat),
        maxLat: parseFloat(this.manualBounds.maxLat)
      };
    },

    /**
     * 生成热力图
     */
    async generateHeatmap() {
      if (!this.timeRange || this.timeRange.length !== 2) {
        this.$message.warning('请选择时间范围');
        return;
      }

      // 校验时间范围：参数有效且跨度不超过90天
      const startTs = new Date(this.timeRange[0]).getTime();
      const endTs = new Date(this.timeRange[1]).getTime();
      if (isNaN(startTs) || isNaN(endTs) || endTs <= startTs) {
        this.$message.warning('时间范围参数无效');
        return;
      }
      if (endTs - startTs > MAX_RANGE_DAYS * 24 * 60 * 60 * 1000) {
        this.$message.warning('查询时间范围不能超过30天');
        return;
      }

      const bounds = this.getQueryBounds();
      if (isNaN(bounds.minLon) || isNaN(bounds.maxLon) ||
          isNaN(bounds.minLat) || isNaN(bounds.maxLat)) {
        this.$message.warning('区域范围参数无效');
        return;
      }

      // 清除旧图层
      this.clearHeatmap();

      this.loading = true;
      this.statusText = '正在查询数据，大范围可能需要数秒...';

      const baseParams = {
        minLon: bounds.minLon,
        minLat: bounds.minLat,
        maxLon: bounds.maxLon,
        maxLat: bounds.maxLat,
        startTime: this.timeRange[0],
        endTime: this.timeRange[1]
      };

      try {
        // 逐船轨迹线（视野过大时后端返回空列表，需提示用户放大地图）
        let tracks = [];
        try {
          const tracksRes = await getVesselTracks(Object.assign({}, baseParams, {
            tolerance: this.trackTolerance
          }));
          if (tracksRes.code === 200 && Array.isArray(tracksRes.data)) {
            tracks = tracksRes.data;
          } else if (tracksRes.code !== 200) {
            console.warn('轨迹线查询失败:', tracksRes.message);
          }
        } catch (e) {
          console.warn('轨迹线查询异常:', e);
        }

        if (tracks.length === 0) {
          const area = Math.abs((bounds.maxLon - bounds.minLon) * (bounds.maxLat - bounds.minLat));
          if (area > MAX_TRACK_AREA) {
            this.statusText = '当前视野过大，已跳过轨迹线；请放大地图后重试';
            this.$message.warning('当前视野过大，请放大地图后再生成轨迹线');
          } else {
            this.statusText = '查询完成，但未找到数据';
            this.$message.info('未找到符合条件的轨迹数据');
          }
          return;
        }

        this.trackCount = tracks.length;
        this.renderTracks(tracks);
        // 不提示航迹条数，仅给出状态
        this.statusText = '轨迹线已生成';
      } catch (error) {
        console.error('生成轨迹线失败:', error);
        this.statusText = `查询失败: ${error.message || '网络错误'}`;
        this.$message.error('生成轨迹线失败: ' + (error.message || '网络错误'));
      } finally {
        this.loading = false;
      }
    },

    /**
     * 轨迹线颜色（按密度等级）：黄 → 橙 → 红
     */
    trackColor(level) {
      const colors = TRACK_DENSITY_COLORS;
      return colors[Math.max(0, Math.min(colors.length - 1, level))];
    },

    /**
     * 轨迹线透明度（按密度等级）：稀疏处通透、主干核心醒目
     */
    trackOpacity(level) {
      const arr = TRACK_DENSITY_OPACITY;
      return arr[Math.max(0, Math.min(arr.length - 1, level))];
    },

    /**
     * 绘制船舶轨迹折线，并按"共轨迹"密度着色。
     * 先用网格统计每个网格内"经过的不同航迹条数"（即共轨迹船数），
     * 再让相邻线段按密度等级着色：稀疏处为黄色，多船重复经过的主干航道为红色。
     */
    renderTracks(tracks) {
      const valid = (tracks || []).filter(t => t && t.coords && t.coords.length >= 3);
      if (valid.length === 0) return;

      // 1) 网格统计：每个网格记录"经过该网格的不同航迹条数"（共轨迹船数）
      const cell = TRACK_DENSITY_CELL_DEG;
      const density = new Map();
      const keyOf = (lon, lat) => Math.floor(lon / cell) + ':' + Math.floor(lat / cell);

      for (let t = 0; t < valid.length; t++) {
        const coords = valid[t].coords;
        const cells = new Set();
        for (let i = 1; i < coords.length; i++) {
          const x0 = coords[i - 1][0], y0 = coords[i - 1][1];
          const x1 = coords[i][0], y1 = coords[i][1];
          const dx = x1 - x0, dy = y1 - y0;
          const len = Math.sqrt(dx * dx + dy * dy);
          const steps = Math.max(1, Math.ceil(len / (cell * 0.5)));
          for (let s = 0; s <= steps; s++) {
            const f = s / steps;
            cells.add(keyOf(x0 + dx * f, y0 + dy * f));
          }
        }
        cells.forEach(k => density.set(k, (density.get(k) || 0) + 1));
      }
      const densOf = (lon, lat) => density.get(keyOf(lon, lat)) || 0;

      // 2) 归一化：以 99 分位为满值，并用幂函数加强对比，使红色只保留给最密集的主干核心
      const vals = Array.from(density.values()).sort((a, b) => a - b);
      const norm = vals.length
        ? Math.max(vals[Math.min(vals.length - 1, Math.floor(vals.length * 0.99))], 1)
        : 1;
      const levels = TRACK_DENSITY_COLORS.length;
      const levelOf = (v) => {
        const r = Math.min(1, Math.max(0, v / norm));
        const curved = Math.pow(r, 2);
        return Math.min(levels - 1, Math.round(curved * (levels - 1)));
      };

      // 3) 逐段着色并合并：同等级且不过长的相邻段合并为一条折线；
      //    在屏幕尺度上过长的线段直接断开，避免在高缩放下出现明显的"长直线"
      const cosLat = Math.cos(this.map.getCenter().lat * Math.PI / 180);
      const mpp = (156543.03392 * cosLat) / Math.pow(2, this.map.getZoom());
      const maxSegPx = TRACK_MAX_SEGMENT_PX;
      const tooLong = (a, b) => {
        const dLat = (b[1] - a[1]) * 110540;
        const dLon = (b[0] - a[0]) * 111320 * cosLat;
        return Math.sqrt(dLat * dLat + dLon * dLon) / mpp > maxSegPx;
      };

      const runs = [];
      for (let t = 0; t < valid.length; t++) {
        const coords = valid[t].coords;
        const segCount = coords.length - 1;
        const segLevels = new Array(segCount);
        for (let s = 0; s < segCount; s++) {
          const a = coords[s], b = coords[s + 1];
          segLevels[s] = levelOf((densOf(a[0], a[1]) + densOf(b[0], b[1])) / 2);
        }
        const pushRun = (from, to) => {
          const latlngs = [];
          for (let j = from; j <= to + 1; j++) {
            latlngs.push([coords[j][1], coords[j][0]]);
          }
          if (latlngs.length >= 2) {
            runs.push({ level: segLevels[from], latlngs });
          }
        };
        let from = 0;
        for (let s = 0; s < segCount; s++) {
          if (tooLong(coords[s], coords[s + 1])) {
            // 过长线段不绘制：结束当前段并跳过该线段
            if (s > from) pushRun(from, s - 1);
            from = s + 1;
            continue;
          }
          const isLast = s === segCount - 1;
          const nextBreaks = !isLast &&
            (segLevels[s + 1] !== segLevels[from] || tooLong(coords[s + 1], coords[s + 2]));
          if (isLast || nextBreaks) {
            pushRun(from, s);
            from = s + 1;
          }
        }
      }

      // 4) 低密度先画、高密度后画，使红色主干压在黄色之上
      runs.sort((a, b) => a.level - b.level);

      // 轨迹层放在底图之上、桥区/标示(overlayPane, z-index 400)之下的专用 pane，
      // 避免轨迹线遮挡桥梁区域与标示
      const paneName = 'heatmapTracksPane';
      if (!this.map.getPane(paneName)) {
        const pane = this.map.createPane(paneName);
        pane.style.zIndex = 250;
        pane.style.pointerEvents = 'none';
      }
      const renderer = L.canvas({ pane: paneName, padding: 0.5 });
      const group = L.layerGroup();
      for (let i = 0; i < runs.length; i++) {
        const run = runs[i];
        if (run.latlngs.length < 2) continue;
        group.addLayer(L.polyline(run.latlngs, {
          renderer,
          color: this.trackColor(run.level),
          weight: 1.3,
          opacity: this.trackOpacity(run.level),
          lineCap: 'round',
          lineJoin: 'round',
          interactive: false
        }));
      }

      this.trackRenderer = renderer;
      this.trackLayer = group;
      this.trackLayer.addTo(this.map);
      this.$emit('track-layer-change', true);
    },

    /**
     * 清除轨迹线图层
     */
    clearHeatmap() {
      if (this.trackLayer && this.map) {
        this.map.removeLayer(this.trackLayer);
      }
      this.trackLayer = null;
      this.trackRenderer = null;
      this.trackCount = 0;
      this.statusText = '';
      this.$emit('track-layer-change', false);
    },

    /**
     * 截图前把 Leaflet 矢量图层的 CSS 变换"拍平"为 left/top 定位，
     * 规避 html2canvas 不处理元素 transform 导致桥区图形位置偏移的问题。
     * 返回还原函数（截图结束后调用）。
     */
    bakeSvgLayers(container) {
      const restores = [];
      try {
        const svgs = Array.prototype.slice.call(container.querySelectorAll('svg'));
        svgs.forEach(svg => {
          try {
            const rect = svg.getBoundingClientRect();
            if (!rect.width || !rect.height) return;
            const parent = svg.parentNode;
            if (!parent) return;
            const parentRect = parent.getBoundingClientRect();
            const prevStyle = svg.getAttribute('style') || '';
            // 用 left/top 还原原本由 transform 承担的位移，并去掉 transform
            svg.style.transform = 'none';
            svg.style.position = 'absolute';
            svg.style.left = (rect.left - parentRect.left) + 'px';
            svg.style.top = (rect.top - parentRect.top) + 'px';
            restores.push(() => { svg.setAttribute('style', prevStyle); });
          } catch (e) {
            console.warn('SVG 变换拍平失败，跳过该图层:', e);
          }
        });
      } catch (e) {
        console.warn('SVG 变换拍平整体失败:', e);
      }
      return () => {
        restores.forEach(fn => { try { fn(); } catch (e) { /* ignore */ } });
      };
    },

    /**
     * 导出地图截图为图片文件
     */
    async exportImage() {
      if (!this.map) return;

      this.exporting = true;
      this.statusText = '正在生成图片...';

      // 导出前隐藏船舶图层，使导出图片仅包含底图与轨迹线
      this.$emit('export-before');
      await this.$nextTick();
      // 等待船舶图层重绘完成
      await new Promise(resolve => setTimeout(resolve, 300));

      try {
        const mapContainer = this.map.getContainer();
        // 截图前把 Leaflet 的 SVG 矢量层（桥区等）光栅化为 <img>，
        // 规避 html2canvas 无法正确处理 SVG <g> 变换、导致桥区位置偏移的问题
        const restoreSvg = await this.bakeSvgLayers(mapContainer);
        let canvas;
        try {
          canvas = await html2canvas(mapContainer, {
            useCORS: true,
            logging: false,
            backgroundColor: null,
            scale: 1
          });
        } finally {
          restoreSvg();
        }

        // 下载图片
        const link = document.createElement('a');
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        link.download = `tracks_${timestamp}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();

        this.statusText = '图片已导出';
        this.$message.success('图片导出成功');
      } catch (error) {
        console.error('导出图片失败:', error);
        // 降级：尝试仅导出轨迹线图层 canvas
        try {
          const trackCanvas = this.trackRenderer && (this.trackRenderer._container || this.trackRenderer._canvas);
          if (trackCanvas) {
            const link = document.createElement('a');
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
            link.download = `tracks_${timestamp}.png`;
            link.href = trackCanvas.toDataURL('image/png');
            link.click();
            this.statusText = '已导出轨迹线图层（不含底图）';
            this.$message.success('已导出轨迹线图层');
          } else {
            this.statusText = `导出失败: ${error.message}`;
            this.$message.error('导出图片失败: ' + error.message);
          }
        } catch (e) {
          this.statusText = `导出失败: ${e.message}`;
          this.$message.error('导出图片失败: ' + e.message);
        }
      } finally {
        // 恢复导出前的船舶图层显示状态
        this.$emit('export-after');
        this.exporting = false;
      }
    }
  }
};
</script>

<style scoped>
.heatmap-panel {
  position: absolute;
  z-index: 1201;
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.15);
  width: 380px;
  max-height: calc(100vh - 100px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  /* 大屏布局中左侧「船舶概览/水文气象」等浮层卡片位于 map-layer 之上，
     面板若靠左会被遮挡，故居中放置到地图可视区中部空档 */
  top: 150px;
  left: 50%;
  transform: translateX(-50%);
}

.heatmap-panel-title {
  padding: 10px 15px;
  background-color: #f0f2f5;
  border-bottom: 1px solid #dcdfe6;
  display: flex;
  align-items: center;
  font-weight: 500;
  user-select: none;
  position: relative;
}

.heatmap-panel-title i {
  margin-right: 8px;
  font-size: 16px;
  color: #606266;
}

.heatmap-panel-content {
  flex: 1;
  overflow-y: auto;
  padding: 15px;
}

.form-row {
  margin-bottom: 12px;
}

.form-row-inline {
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
}

.form-row-item {
  flex: 1;
}

.form-label {
  display: block;
  font-size: 13px;
  color: #606266;
  margin-bottom: 5px;
  font-weight: 500;
}

.form-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 5px;
}

.bounds-info {
  background-color: #f4f4f5;
  padding: 8px 10px;
  border-radius: 3px;
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
}

.bounds-manual {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.bounds-input-row {
  display: flex;
  gap: 5px;
}

.button-row {
  display: flex;
  gap: 8px;
  margin-top: 15px;
}

.status-area {
  margin-top: 12px;
  padding: 8px 10px;
  background-color: #f4f4f5;
  border-radius: 3px;
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
}

.status-area i {
  margin-right: 6px;
  color: #409EFF;
}
</style>
